import { useTheme } from '@emotion/react'
import { Add, FilterList, PushPin, UnfoldMore } from '@mui/icons-material'
import {
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import { amber } from '@mui/material/colors'
import startCase from 'lodash.startcase'
import { useRef, useState } from 'react'
import type { CandidateNote } from 'recruiter/graphql/candidateNotes.gql'
import { getPxAsRem } from 'shared/utils/style'

export enum SORT_ORDER {
    newest = 'newest',
    oldest = 'oldest',
    topRated = 'top rated',
}

interface NotesToolbarProps {
    selectedFilter: string | null
    sortOrder: SORT_ORDER | null
    searchValue: string
    pinnedNotes: CandidateNote[]
    onFilterChange: (filter: string | null) => void
    onSortChange: (sort: SORT_ORDER | null) => void
    onSearchChange: (value: string) => void
    onAddNote: () => void
    showFilters: boolean
    onToggleFilters: () => void
    notesCount: number
    onSelectNote: (noteId: string) => void
}

const NotesToolbar = ({
    selectedFilter,
    sortOrder,
    searchValue,
    pinnedNotes,
    onFilterChange,
    onSortChange,
    onSearchChange,
    onAddNote,
    showFilters,
    onToggleFilters,
    notesCount,
    onSelectNote,
}: NotesToolbarProps) => {
    const theme = useTheme()
    const sortRef = useRef<HTMLButtonElement>(null)
    const [sortMenuOpen, setSortMenuOpen] = useState(false)

    const handleSortSelection = (newSortOrder: SORT_ORDER) => {
        setSortMenuOpen(false)
        onSortChange(sortOrder === newSortOrder ? null : newSortOrder)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
                padding: getPxAsRem(16),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <Typography variant="h4">Notes</Typography>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.grey[300],
                        color: (theme) => theme.palette.grey[700],
                        borderRadius: '50%',
                        minWidth: 20,
                        minHeight: 20,
                        aspectRatio: '1/1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        position: 'relative',
                        top: 1,
                        paddingX: 0.5,
                    }}
                >
                    {notesCount}
                </Box>
            </Box>

            {pinnedNotes.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.5,
                        overflow: 'hidden',
                        flex: 1,
                        minWidth: 100,
                    }}
                >
                    {pinnedNotes.map((note) => (
                        <Chip
                            key={note.noteId}
                            icon={<PushPin sx={{ height: 12 }} />}
                            label={
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {new Date(
                                        note.createdAt
                                    ).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                    })}{' '}
                                    •{' '}
                                    {startCase(note.template?.templateType) ||
                                        'Note'}
                                </Box>
                            }
                            size="small"
                            onClick={() => onSelectNote(note.noteId!)}
                            sx={{
                                flexShrink: 1,
                                minWidth: 0,
                                backgroundColor: amber[50],
                                borderColor: amber[300],
                                cursor: 'pointer',
                            }}
                            variant="outlined"
                        />
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                    ml: 'auto',
                }}
            >
                {selectedFilter ? (
                    <Chip
                        icon={<FilterList sx={{ fontSize: 16 }} />}
                        label={startCase(selectedFilter)}
                        size="small"
                        onClick={onToggleFilters}
                        onDelete={() => onFilterChange(null)}
                        sx={{
                            height: 28,
                            cursor: 'pointer',
                            backgroundColor: showFilters
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            color: 'white',
                            '& .MuiChip-icon': {
                                color: 'white',
                            },
                            '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem',
                            },
                            '& .MuiChip-deleteIcon': {
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: 16,
                                '&:hover': {
                                    color: 'white',
                                },
                            },
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    />
                ) : (
                    <IconButton
                        size="small"
                        onClick={onToggleFilters}
                        sx={{
                            p: 0.5,
                            backgroundColor: showFilters
                                ? theme.palette.primary.light
                                : undefined,
                        }}
                    >
                        <FilterList sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
                <IconButton
                    size="small"
                    ref={sortRef}
                    onClick={() => setSortMenuOpen((prev) => !prev)}
                    color={sortOrder ? 'primary' : 'default'}
                    sx={{
                        p: 0.5,
                        backgroundColor: sortMenuOpen
                            ? `${theme.palette.primary.light} !important`
                            : undefined,
                    }}
                >
                    <UnfoldMore sx={{ fontSize: 18 }} />
                </IconButton>
                <Menu
                    open={sortMenuOpen}
                    anchorEl={sortRef.current}
                    onClose={() => setSortMenuOpen(false)}
                >
                    <MenuItem
                        onClick={() => handleSortSelection(SORT_ORDER.newest)}
                        selected={sortOrder === SORT_ORDER.newest}
                    >
                        Newest
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleSortSelection(SORT_ORDER.oldest)}
                        selected={sortOrder === SORT_ORDER.oldest}
                    >
                        Oldest
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleSortSelection(SORT_ORDER.topRated)}
                        selected={sortOrder === SORT_ORDER.topRated}
                    >
                        Top Rated
                    </MenuItem>
                </Menu>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={onAddNote}
                    title="Add Note"
                    sx={{ p: 0.5 }}
                >
                    <Add sx={{ fontSize: 18 }} />
                </IconButton>
                <TextField
                    size="small"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    sx={{
                        ml: 0.5,
                        '& .MuiInputBase-root': {
                            height: 28,
                            fontSize: '0.75rem',
                            backgroundColor: (theme) => theme.palette.grey[50],
                        },
                        '& .MuiInputBase-input': {
                            py: 0.5,
                            px: 1,
                        },
                    }}
                />
            </Box>
        </Box>
    )
}

export default NotesToolbar
